/**
 * One-time spotlight modal shown on first login after the user has not yet
 * decided about push notifications + PWA install. Complements the persistent
 * banner (DashboardLayout) and the Settings toggle (Profile.jsx).
 *
 * Shows when ALL of:
 *   • Notification API supported
 *   • Notification.permission === "default"  (user hasn't decided)
 *   • localStorage flag `push_modal_seen` is absent
 *
 * Flag set as soon as user takes any action (enable / install / dismiss),
 * so it never reopens. Banner continues to nag every 24h thereafter.
 */
import { useEffect, useState } from "react";
import { Bell, Smartphone, X, Sparkles, Calendar, FileSignature, MessagesSquare, Gavel } from "lucide-react";
import { registerPush } from "@/lib/push";
import { toast } from "sonner";

const SEEN_KEY = "push_modal_seen";

export default function PushInstallModal() {
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [installPrompt, setInstallPrompt] = useState(null);

    useEffect(() => {
        if (typeof Notification === "undefined") return;
        let seen = "";
        try { seen = localStorage.getItem(SEEN_KEY) || ""; } catch { /* ignore */ }
        if (seen) return;
        if (Notification.permission !== "default") return;
        // Defer 1.2s so it doesn't fight the dashboard's first paint
        const t = setTimeout(() => setShow(true), 1200);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setInstallPrompt(e);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
    }, []);

    const markSeen = () => {
        try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch { /* ignore */ }
        setShow(false);
    };

    const enable = async () => {
        setBusy(true);
        try {
            await registerPush();
            if (Notification.permission === "granted") {
                toast.success("Notifications enabled — you'll never miss a hearing.");
            } else if (Notification.permission === "denied") {
                toast.error("Browser blocked notifications. You can change this in browser settings.");
            }
        } catch { toast.error("Could not enable notifications"); }
        finally { setBusy(false); markSeen(); }
    };

    const install = async () => {
        if (!installPrompt) {
            toast.message("To install, use your browser menu → 'Install app' or 'Add to home screen'.");
            return;
        }
        try {
            installPrompt.prompt();
            const choice = await installPrompt.userChoice;
            if (choice?.outcome === "accepted") toast.success("Law OS installed");
        } catch { /* user dismissed */ }
        setInstallPrompt(null);
    };

    if (!show) return null;
    return (
        <div
            className="fixed inset-0 z-[110] bg-background/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="push-install-modal"
            onClick={markSeen}
        >
            <div
                className="bg-card border border-primary/40 max-w-md w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative px-6 pt-6 pb-4 border-b border-[#1F1F1F]">
                    <button
                        onClick={markSeen}
                        data-testid="push-modal-close"
                        className="absolute top-4 right-4 p-1 text-[#7A7A7A] hover:text-foreground"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" strokeWidth={1.5}/>
                    </button>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-primary mb-2">
                        <Sparkles className="w-3 h-3"/>Welcome to Law OS
                    </div>
                    <h2 className="text-xl font-semibold text-foreground leading-snug">
                        Don&apos;t miss a single hearing.
                    </h2>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                        Get real-time alerts on the moments that matter — directly to your device, even when Law OS is closed.
                    </p>
                </div>

                {/* Triggers */}
                <div className="px-6 py-5 space-y-3">
                    {[
                        { Icon: Gavel,         label: "Hearings & cause-list updates" },
                        { Icon: Calendar,      label: "Tomorrow's tasks & deadlines" },
                        { Icon: MessagesSquare,label: "Client chat replies" },
                        { Icon: FileSignature, label: "Document signature requests" },
                    ].map(({ Icon, label }) => (
                        <div key={label} className="flex items-center gap-3 text-sm text-[#E5E5E5]">
                            <div className="w-8 h-8 bg-primary/10 border border-primary/20 flex items-center justify-center">
                                <Icon className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                            </div>
                            <span>{label}</span>
                        </div>
                    ))}
                </div>

                {/* Actions */}
                <div className="px-6 pb-6 pt-2 space-y-2">
                    <button
                        onClick={enable}
                        disabled={busy}
                        data-testid="push-modal-enable"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-semibold uppercase tracking-[0.15em]"
                    >
                        <Bell className="w-3.5 h-3.5"/>{busy ? "Requesting…" : "Enable notifications"}
                    </button>
                    {installPrompt && (
                        <button
                            onClick={install}
                            data-testid="push-modal-install"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-transparent border border-primary/40 text-primary hover:bg-primary/10 text-xs font-semibold uppercase tracking-[0.15em]"
                        >
                            <Smartphone className="w-3.5 h-3.5"/>Install Law OS as app
                        </button>
                    )}
                    <button
                        onClick={markSeen}
                        data-testid="push-modal-later"
                        className="w-full text-[11px] text-[#7A7A7A] hover:text-foreground py-1.5"
                    >
                        Maybe later
                    </button>
                </div>
            </div>
        </div>
    );
}
